import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTeams } from "@/contexts/teams-provider";
import { UserRoundPlusIcon } from "lucide-react";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@radix-ui/react-icons";
import { CompanyDTO, UserDTO } from "@shaarp/common";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { useToastError } from "../ui/use-toast";

export default function CompanyUserAssignedSelector({
  company,
  selectedUser,
  onSelectedUserChange,
  onChange = () => {},
  onActionComplete,
  triggerClassName,
  triggerLabel,
  defaultOpen = false,
}: {
  company: CompanyDTO;
  selectedUser?: UserDTO | null;
  onSelectedUserChange?: (user: UserDTO | null) => void;
  onChange?: (newUserId: string | null) => void;
  onActionComplete?: () => void;
  triggerClassName?: string;
  triggerLabel?: string;
  /** Opens the picker as it mounts: the click that asked for it saved the company first. */
  defaultOpen?: boolean;
}) {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const { isUser } = useTeams();
  const { users, apiFetchUsers } = useUsers();
  const { teamUser } = useUser();
  const { isMobile } = useDeviceFormat();

  const [open, setOpen] = useState(defaultOpen);
  const [localUserSelected, setLocalUserSelected] = useState<UserDTO | null>(null);

  const isControlled = selectedUser !== undefined;
  const userSelected = isControlled ? selectedUser : localUserSelected;
  const assignableUsers = users.filter((u) => u.id !== teamUser?.id);

  const hasPermissionAssignOther = () => {
    return !isUser;
  };

  const assign = async (newUser: UserDTO | null) => {
    setOpen(false);
    if (isControlled) {
      onSelectedUserChange?.(newUser);
    } else {
      setLocalUserSelected(newUser);
    }
    onChange(newUser ? newUser.id : null);
    try {
      await api.put(`/company/${company.id}/assign`, {
        userId: newUser ? newUser.id : "",
      });
      onActionComplete?.();
    } catch (e) {
      toastError();
    }
  };

  useEffect(() => {
    const init = async () => {
      await apiFetchUsers();
    };
    init();
  }, []);

  useEffect(() => {
    const resolved = users.find((u) => u.id === company.assignation?.userId) || null;
    if (isControlled) {
      onSelectedUserChange?.(resolved);
    } else {
      setLocalUserSelected(resolved);
    }
  }, [company.assignation?.userId, teamUser, users]);

  const renderTrigger = (onClick?: () => void) =>
    triggerLabel ? (
      <Button
        variant="outline"
        className={cn(
          "h-10 gap-2 px-3",
          triggerClassName,
        )}
        onClick={onClick}
      >
        {userSelected ? (
          <Avatar className="w-5 h-5 shrink-0">
            <AvatarImage
              src={userSelected.imageUrl}
              alt={userSelected.fullName}
              className="object-cover"
            />
            <AvatarFallback>{userSelected.fullName}</AvatarFallback>
          </Avatar>
        ) : (
          <UserRoundPlusIcon className="w-4 h-4 shrink-0" />
        )}
        <span className="text-sm truncate">
          {userSelected
            ? t("company.userAssignedSelector.assignedTo", {
                name: userSelected.fullName,
              })
            : triggerLabel}
        </span>
      </Button>
    ) : (
      <Avatar
        className={cn(
          "w-10 h-10 cursor-pointer border",
          userSelected ? "border-primary" : "border-border",
          triggerClassName,
        )}
        onClick={onClick}
      >
        <AvatarImage
          src={userSelected?.imageUrl}
          alt={userSelected?.fullName}
        />
        <AvatarFallback>
          {userSelected ? (
            teamUser?.fullName
          ) : (
            <UserRoundPlusIcon className="w-5 h-5 text-muted-foreground" />
          )}
        </AvatarFallback>
      </Avatar>
    );

  const commandContent = (
    <Command>
      <CommandInput
        placeholder={t("company.userAssignedSelector.search")}
        classNameWrapper={isMobile ? "px-4" : undefined}
        className={isMobile ? "h-12 text-base" : undefined}
      />
      <CommandList className={isMobile ? "max-h-none flex-1" : undefined}>
        {teamUser && (
          <>
            <CommandGroup>
              <CommandItem
                key={teamUser.fullName}
                value={teamUser.fullName || ""}
                disabled={false}
                onSelect={() => assign(teamUser)}
                className={isMobile ? "py-3 px-3" : undefined}
              >
                <Avatar
                  className={cn("mr-3", isMobile ? "w-8 h-8" : "w-6 h-6")}
                >
                  <AvatarImage
                    src={teamUser.imageUrl}
                    alt={teamUser.fullName}
                    className="object-cover"
                  />
                  <AvatarFallback>{teamUser.fullName}</AvatarFallback>
                </Avatar>
                <span className={isMobile ? "text-[15px]" : undefined}>
                  {t("company.userAssignedSelector.assignToMe")}
                </span>
                <CheckIcon
                  className={cn(
                    "ml-auto h-4 w-4",
                    teamUser.id === userSelected?.id
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup>
          <CommandItem
            key={"Non assigné"}
            value={"Non assigné"}
            onSelect={() => assign(null)}
            className={isMobile ? "py-3 px-3" : undefined}
          >
            <Avatar className={cn("mr-3", isMobile ? "w-8 h-8" : "w-6 h-6")}>
              <AvatarFallback>
                {t("company.userAssignedSelector.notAssignedAbbr")}
              </AvatarFallback>
            </Avatar>
            <span className={isMobile ? "text-[15px]" : undefined}>
              {t("company.userAssignedSelector.notAssigned")}
            </span>
            <CheckIcon
              className={cn(
                "ml-auto w-4 h-4",
                !userSelected ? "opacity-100" : "opacity-0",
              )}
            />
          </CommandItem>
        </CommandGroup>
        {hasPermissionAssignOther() && assignableUsers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup>
              {assignableUsers.map((u) => (
                <CommandItem
                  key={u.fullName}
                  value={u.fullName}
                  disabled={false}
                  onSelect={() => assign(u)}
                  className={isMobile ? "py-3 px-3" : undefined}
                >
                  <Avatar
                    className={cn("mr-3", isMobile ? "w-8 h-8" : "w-6 h-6")}
                  >
                    <AvatarImage src={u.imageUrl} alt={u.fullName} />
                    <AvatarFallback>{u.fullName}</AvatarFallback>
                  </Avatar>
                  <span className={isMobile ? "text-[15px]" : undefined}>
                    {u.fullName}
                  </span>
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      u.id === userSelected?.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <>
        {renderTrigger(() => setOpen(true))}
        <MobileSheet open={open} onOpenChange={setOpen} heightClass="h-[80dvh]">
          <div className="px-5 pt-2 pb-3 shrink-0">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {t("company.userAssignedSelector.assignCompany")}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("company.userAssignedSelector.assignCompany")}
            </SheetDescription>
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {commandContent}
          </div>
        </MobileSheet>
      </>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            {userSelected
              ? t("company.userAssignedSelector.assignedTo", {
                  name: userSelected.fullName,
                })
              : t("company.userAssignedSelector.notAssigned")}
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="p-0" side="bottom" align="center">
          {commandContent}
        </PopoverContent>
      </Popover>
    </>
  );
}
